import { resourcesCopy } from '@/copy/resources'
import { TextField } from '@/ui/Field/Field'
import { NormalizeRulesEditor } from './NormalizeRulesEditor'
import { type RuleDraft, ruleBody, ruleDrafts } from './normalizeRules'
import {
  type FieldProps,
  type Fields,
  fieldStr,
  preservedKeys,
  STORED,
} from './shared'

// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function WebhookFields({ fields, set, isEdit }: FieldProps) {
  return (
    <>
      <TextField
        id="res-event-type"
        label={resourcesCopy.forms.webhook.eventType}
        help={resourcesCopy.forms.webhook.eventTypeHelp}
        value={fields.event_type ?? ''}
        onValue={(v) => set('event_type', v)}
        inputProps={{
          placeholder: resourcesCopy.forms.webhook.eventTypePlaceholder,
        }}
      />
      <TextField
        id="res-secret"
        label={resourcesCopy.forms.webhook.secret}
        hint={
          isEdit
            ? resourcesCopy.form.leaveBlankToKeep
            : resourcesCopy.form.optional
        }
        help={resourcesCopy.forms.webhook.secretHelp}
        value={fields.webhook_secret ?? ''}
        onValue={(v) => set('webhook_secret', v)}
        inputProps={{ type: 'password' }}
      />
      <TextField
        id="res-signature-header"
        label={resourcesCopy.forms.webhook.signatureHeader}
        value={fields.signature_header ?? ''}
        onValue={(v) => set('signature_header', v)}
        inputProps={{
          placeholder: resourcesCopy.forms.webhook.signatureHeaderPlaceholder,
        }}
      />
      <TextField
        id="res-signature-prefix"
        label={resourcesCopy.forms.webhook.signaturePrefix}
        help={resourcesCopy.forms.webhook.signaturePrefixHelp}
        value={fields.signature_prefix ?? 'sha256='}
        onValue={(v) => set('signature_prefix', v)}
      />
      <NormalizeRulesEditor
        rules={JSON.parse(fields.normalize || '[]') as RuleDraft[]}
        onRules={(rules) => set('normalize', JSON.stringify(rules))}
      />
      <TextField
        id="res-desc"
        label={resourcesCopy.forms.webhook.description}
        hint={resourcesCopy.form.optional}
        value={fields.description ?? ''}
        onValue={(v) => set('description', v)}
      />
    </>
  )
}

export const webhookForm = {
  Fields: WebhookFields,
  // Stored keys the form doesn't render (normalize rules, signature and
  // delivery headers, …) ride along: the update API replaces `data`.
  toBody: (f: Fields) => ({
    ...preservedKeys(f),
    event_type: f.event_type || 'webhook.received',
    // Cleared → undefined → dropped from the JSON: the default applies.
    signature_header: f.signature_header?.trim() || undefined,
    signature_prefix: f.signature_prefix ?? 'sha256=',
    normalize: (JSON.parse(f.normalize || '[]') as RuleDraft[]).map(ruleBody),
    description: f.description ?? '',
    ...(f.webhook_secret ? { webhook_secret: f.webhook_secret } : {}),
  }),
  // The secret is write-only and never returned; its input stays blank.
  fromResource: (d: unknown) => ({
    [STORED]: JSON.stringify(d ?? {}),
    event_type: fieldStr(d, 'event_type'),
    signature_header: fieldStr(d, 'signature_header'),
    signature_prefix: fieldStr(d, 'signature_prefix', 'sha256='),
    normalize: JSON.stringify(
      ruleDrafts((d as Record<string, unknown> | null)?.normalize),
    ),
    description: fieldStr(d, 'description'),
  }),
}
